<script setup lang="ts">
import type { User } from 'src/modules/User/models/User';
import { computed } from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import BAdminInput from 'src/shared/components/base/BAdminInput/BAdminInput.vue';
import BAdminSelect from 'src/shared/components/base/BAdminSelect/BAdminSelect.vue';
import { useUserSettings } from '../../useUserSettings';
import useValidators from 'src/shared/composables/useValidators';
import { useSelectOptions } from 'src/shared/composables/strapi/useSelectOptions';

const $q = useQuasar();
const { t } = useI18n();
const { required, mobile, min, max, positiveNumber, digitsOnly, namePattern } =
  useValidators();

const user = defineModel<Partial<User>>();
const { address, phone } = useUserSettings(user);
const useDarkFields = computed(() => $q.dark.isActive);

const { options: cities, search, loading } = useSelectOptions('cities', 'city');

// function filterOptions(val: string, update: (callback: () => void) => void) {
//   update(() => {
//     search.value = val;
//   });
// }

const emit = defineEmits<{
  submit: [];
  close: [];
}>();
</script>

<template>
  <q-form
    autofocus
    :class="{ 'profile-form--dark': useDarkFields }"
    @submit.prevent="$emit('submit')"
  >
    <div
      v-if="user"
      class="row q-col-gutter-sm"
    >
      <BAdminInput
        v-model="user.firstname"
        class="col-12 col-sm-6"
        :label="t('FIRST_NAME')"
        :rules="[
          required,
          namePattern,
          (val: string) => min(val, 2),
          (val: string) => max(val, 32),
        ]"
        :dark="useDarkFields"
      />
      <BAdminInput
        v-model="user.lastname"
        class="col-12 col-sm-6"
        :label="t('LAST_NAME')"
        :rules="[
          required,
          namePattern,
          (val: string) => min(val, 2),
          (val: string) => max(val, 32),
        ]"
        :dark="useDarkFields"
      />
    </div>

    <template v-if="address">
      <q-separator />
      <h3 class="text-h6 q-my-md q-px-sm text-uppercase">
        {{ t('ADDRESS') }}
      </h3>
      <div class="row q-col-gutter-sm">
        <BAdminInput
          v-model="address.number"
          type="number"
          class="col-12 col-sm-4"
          :label="t('STREET_NUMBER')"
          :rules="[required, positiveNumber]"
          :dark="useDarkFields"
        />

        <BAdminInput
          v-model="address.street"
          class="col-12 col-sm-8"
          :label="t('STREET')"
          :rules="[
            required,
            (val: string) => min(val, 2),
            (val: string) => max(val, 128),
          ]"
          :dark="useDarkFields"
        />

        <BAdminSelect
          v-model="address.city"
          class="col-12 col-sm-5"
          :label="t('CITY')"
          :loading="loading"
          :options="cities"
          option-label="city"
          option-value="city"
          :rules="[required]"
          use-input
          emit-value
          map-options
          input-debounce="0"
          :dark="useDarkFields"
          @filter="(val: string, update: any) => update(() => (search = val))"
        />

        <BAdminSelect
          v-model="address.country"
          class="col-12 col-sm-5"
          :label="t('COUNTRY')"
          :options="['Ukraine', 'Russia', 'Israel']"
          :rules="[required]"
          :dark="useDarkFields"
        />

        <BAdminInput
          v-model="address.zip"
          class="col-12 col-sm-5"
          :label="t('ZIPCODE')"
          :rules="[
            required,
            digitsOnly,
            (val: string) => min(val, 5),
            (val: string) => max(val, 10),
          ]"
          :dark="useDarkFields"
        />
      </div>
    </template>

    <template v-if="phone">
      <q-separator :dark="useDarkFields" />
      <h3 class="text-h6 q-my-md q-px-sm text-uppercase">
        {{ t('PHONE') }}
      </h3>
      <div class="row q-col-gutter-sm">
        <BAdminInput
          v-model="phone.number"
          class="col-12 col-sm-6"
          :label="t('PHONE_NUMBER')"
          :rules="[required, mobile]"
          :dark="useDarkFields"
        />
      </div>
    </template>

    <q-separator />

    <div class="row form-actions q-gutter-md q-py-md">
      <q-btn
        type="submit"
        color="primary"
        :label="t('SAVE')"
        :dark="useDarkFields"
      />

      <q-btn
        :label="t('CANCEL')"
        @click="emit('close')"
      />
    </div>
  </q-form>
</template>

<style scoped lang="scss">
/* Quasar's dark `filled` fill is very low-contrast against the dark card; lift
   the field surface so inputs read as distinct boxes. */
.profile-form--dark :deep(.q-field--filled .q-field__control) {
  background: var(--surface-elevated);
}
.profile-form--dark :deep(.q-field--filled .q-field__control:hover) {
  background: #2f2f2f;
}
</style>
